import React from 'react';

import { Control } from '@vlabs/uikit';
import { Select } from '@vlabs/uikit/components/controls/select/Select';
import { Controller, useFormContext } from 'react-hook-form';

import {
  AutorestartFields,
  STREAM_SETTINGS_SYNC_FIELDS,
  SynchronizeStatus,
  TimestampFields,
} from '@vlabs/monokit/entities/sources';
import { FormFieldGroup } from '@vlabs/monokit/shared/ui/form-field-group';

import { useSourceStreamSettingsTranslation } from './i18n';
import st from './SourceStreamSettings.module.sass';
import type { StreamSettingsFormValues } from '../source-stream-general-info/types';

interface SourceStreamSettingsProps {
  /** Пофилдовые статусы синхронизации с группой (путь → true наследует/false собственные). */
  syncStatus?: Record<string, boolean>;
}

interface Option<TValue extends string | number> {
  value: TValue;
  label: string;
}

function SourceStreamSettings({
  syncStatus = {},
}: SourceStreamSettingsProps): React.JSX.Element {
  const { t } = useSourceStreamSettingsTranslation();
  const {
    control,
    register,
    watch,
    formState: { errors },
  } = useFormContext<StreamSettingsFormValues>();

  const typeOptions: Option<string>[] = [
    { value: 'stream', label: t('type_options.stream') },
    { value: 'videofile', label: t('type_options.videofile') },
  ];

  const autorestartEnabled = watch('autorestart.restart');

  const getSyncStatus = (path: string): boolean | undefined => syncStatus[path];
  const restartSyncStatus = getSyncStatus(STREAM_SETTINGS_SYNC_FIELDS.restart);

  return (
    <div className={st.Root}>
      <div className={st.Section}>
        <h6 className={st.SectionTitle}>{t('data_title')}</h6>

        <FormFieldGroup>
          <FormFieldGroup.Item
            label={t('fields.type')}
            required
            tooltip={t('tooltips.type')}
          >
            <Controller
              control={control}
              name="data.type"
              render={({ field: { value, onChange } }) => (
                <Select
                  classNamePrefix="SourceStreamType"
                  errorMessage={errors.data?.type?.message}
                  errors={errors}
                  menuPortalTarget={document.body}
                  onChange={(option) => onChange(option?.value ?? undefined)}
                  options={typeOptions}
                  placeholder={t('type_options.stream')}
                  value={typeOptions.find((option) => option.value === value)}
                />
              )}
              rules={{ required: t('validation.required') }}
            />
          </FormFieldGroup.Item>

          <FormFieldGroup.Item
            label={t('fields.reference')}
            required
            tooltip={t('tooltips.reference')}
          >
            <Control.Input
              errorMessage={errors.data?.reference?.message}
              errors={errors}
              id="reference"
              {...register('data.reference', { required: t('validation.required') })}
            />
          </FormFieldGroup.Item>

          <TimestampFields
            getSyncStatus={getSyncStatus}
            namePrefix="data"
          />
        </FormFieldGroup>
      </div>

      <div className={st.Section}>
        <h6 className={st.SectionTitle}>{t('autorestart_title')}</h6>

        <FormFieldGroup>
          <FormFieldGroup.Item
            label={(
              <React.Fragment>
                {t('fields.restart')}
                {restartSyncStatus !== undefined && (
                  <SynchronizeStatus synchronized={restartSyncStatus} />
                )}
              </React.Fragment>
            )}
          >
            <Control.Switch
              control={control}
              name="autorestart.restart"
            />
          </FormFieldGroup.Item>

          {!!autorestartEnabled && (
            <AutorestartFields
              getSyncStatus={getSyncStatus}
              namePrefix="autorestart"
            />
          )}
        </FormFieldGroup>
      </div>
    </div>
  );
}

export { SourceStreamSettings };
